import { BaseRepository } from './BaseRepository'
import { newId } from './ids'

/**
 * TasksRepository — re-implementacion limpia (SQL) de los ops de task de v1
 * src/offline/modules/tasks.js + task-actions.js (REFERENCIA — NO importado).
 *
 * Tabla `tasks`, entity `task`, pk `client_uuid`. TODOS los ops de task son EVENTS:
 * NO se coalescen — cada uno obtiene su propia fila de outbox (camino publico
 * saveOptimistic / executeSet). Bodies NORMALIZADOS (F3 construye el REST y resuelve
 * client_uuid->server_id al drenar). Las marcas tcreated:/tedit:/tstatus: las escribe
 * F3 al exito — NO aqui.
 *
 * NUANCE CRITICA (research §"Critical nuance"): cada EDIT (updateFields) usa una
 * IDENTIDAD de outbox UNICA por edicion para que dos ediciones antes de reconectar
 * AMBAS sincronicen (FIFO, el ultimo gana en server). changeStatus dedup-por-estado
 * (identidad estable por (client_uuid, state)) para que re-aplicar el mismo estado
 * sea idempotente.
 */
export class TasksRepository extends BaseRepository {
  constructor () {
    super('tasks', 'task', 'client_uuid')
  }

  /**
   * Crea una task in-occurrence (EVENT). op task.create, body { task }.
   * @param {object} taskRow  fila de la task (incluye client_uuid).
   * @returns {Promise<string>} client_uuid.
   */
  async create (taskRow = {}, bodyExtra = {}) {
    const uuid = taskRow.client_uuid
    // `bodyExtra` = campos body-only (form_id, tag2): entran al body del op (los lee buildCreatePayload/
    // fromForms) pero NUNCA a la dataRow (no son columnas de `tasks`). dataRow queda limpia para el UPSERT.
    return this.saveOptimistic({
      clientUuid: uuid,
      dataRow: { ...taskRow },
      op: 'task.create',
      coalesce: false,
      body: { task: { ...taskRow, ...bodyExtra } }
    })
  }

  /**
   * Crea una task standalone (EVENT). op task.createStandalone, body { task }.
   * @param {object} taskRow  fila de la task (incluye client_uuid).
   * @returns {Promise<string>} client_uuid.
   */
  async createStandalone (taskRow = {}) {
    const uuid = taskRow.client_uuid
    return this.saveOptimistic({
      clientUuid: uuid,
      dataRow: { ...taskRow },
      op: 'task.createStandalone',
      coalesce: false,
      body: { task: { ...taskRow } }
    })
  }

  /**
   * Edita campos de una task (EVENT, NUNCA coalesce). CRITICO: cada edicion lleva un
   * marcador UNICO (edit_uuid = newId()) en el body para que dos ediciones antes de
   * reconectar ambas sincronicen. server_id lo resuelve F3 al drenar (null aqui).
   * @param {string} clientUuid  id local de la task.
   * @param {object} changes     campos modificados.
   * @returns {Promise<string>} client_uuid.
   */
  async updateFields (clientUuid, dataChanges = {}, bodyChanges = dataChanges) {
    // PR review #4: `dataChanges` debe ser SOLO columnas reales de la tabla `tasks` (van al UPSERT);
    // `bodyChanges` conserva las claves que espera el builder REST (buildUpdateFieldsPayload acepta
    // tag/tag2/role/category/...). Antes se pasaba un único `changes` a AMBOS -> claves como
    // tag/role/users (no columnas) reventaban el INSERT con 'no such column' y la edición offline se
    // perdía. El default bodyChanges=dataChanges preserva el comportamiento de callers de un solo arg.
    return this.saveOptimistic({
      clientUuid,
      dataRow: { client_uuid: clientUuid, ...dataChanges },
      op: 'task.updateFields',
      coalesce: false,
      body: { edit_uuid: newId(), server_id: null, client_uuid: clientUuid, changes: bodyChanges }
    })
  }

  /**
   * Cambia el estado de una task (completar/suspender) — dedup-por-estado.
   * Identidad estable por (client_uuid, state) en el body para que re-aplicar el mismo
   * estado dedupe. NO coalesce (es un event), pero el body lleva una marca estable.
   * @param {string} clientUuid
   * @param {string} state
   * @param {object} [fields]
   * @returns {Promise<string>} client_uuid.
   */
  async changeStatus (clientUuid, state, fields = {}) {
    return this.saveOptimistic({
      clientUuid,
      dataRow: { client_uuid: clientUuid, state },
      op: 'task.changeStatus',
      coalesce: false,
      body: { status_key: `${clientUuid}:${state}`, client_uuid: clientUuid, state, ...fields }
    })
  }

  /**
   * Borra una task in-occurrence (EVENT). op task.delete, body { client_uuid }.
   * Marca la fila de datos como borrada atomicamente con el outbox op.
   * @param {string} clientUuid
   * @returns {Promise<string>} client_uuid.
   */
  async remove (clientUuid) {
    return this.saveOptimistic({
      clientUuid,
      dataRow: { client_uuid: clientUuid, state: 'deleted' },
      op: 'task.delete',
      coalesce: false,
      body: { client_uuid: clientUuid }
    })
  }

  /**
   * Borra una task standalone (EVENT). op task.deleteStandalone.
   * @param {string} clientUuid
   * @returns {Promise<string>} client_uuid.
   */
  async removeStandalone (clientUuid) {
    return this.saveOptimistic({
      clientUuid,
      dataRow: { client_uuid: clientUuid, state: 'deleted' },
      op: 'task.deleteStandalone',
      coalesce: false,
      body: { client_uuid: clientUuid }
    })
  }
}

export const tasksRepository = new TasksRepository()
